import { useActionButtonsUiState } from '@/components/action-buttons/use-action-buttons-ui-state';
import { useActionButtonsViewModel } from '@/components/action-buttons/use-action-buttons-view-model';
import { useAiAnalysisActions } from '@/components/action-buttons/use-ai-analysis-actions';
import { useAiAnalysisOrchestration } from '@/components/action-buttons/use-ai-analysis-orchestration';
import { useBrandProductGroupResolver } from '@/components/action-buttons/use-brand-product-group-resolver';
import { useCombinedAnalysisOrchestration } from '@/components/action-buttons/use-combined-analysis-orchestration';
import { useExportActions } from '@/components/action-buttons/use-export-actions';
import { useGroupMergeActions } from '@/components/action-buttons/use-group-merge-actions';
import { useGroupMergeModalOrchestration } from '@/components/action-buttons/use-group-merge-modal-orchestration';
import { usePickedReviewsRef } from '@/components/action-buttons/use-picked-reviews-ref';
import { useSelectionActions } from '@/components/action-buttons/use-selection-actions';
import { useActionButtonsControllerContext } from '@/components/action-buttons/use-action-buttons-controller-context';
import { useActionButtonsControllerProgress } from '@/components/action-buttons/use-action-buttons-controller-progress';
import type { ActionButtonsControllerParams } from '@/components/action-buttons/action-buttons-controller.types';
import type { BuildActionButtonsControllerBindingsArgs } from '@/components/action-buttons/action-buttons-controller-bindings.builder';

export const useActionButtonsControllerOrchestration = ({
    activeTable,
}: ActionButtonsControllerParams): BuildActionButtonsControllerBindingsArgs => {
    const uiState = useActionButtonsUiState();
    const {
        query,
        selection,
        data,
        progress,
        transport,
        reviewAnswerJobs,
        featureControls,
        aiQuota,
        featureAvailability,
        accessToken,
        events,
        isSocketConnected,
    } = useActionButtonsControllerContext();
    const { filtersQuery, sort, refetchReviewsAndProducts, maxSelectedItems } = query;
    const {
        selectionSessionId,
        pickedReviews,
        pickedProducts,
        pickedBrandGroups,
        setPickedReviews,
        setPickedProducts,
        setPickedBrandGroups,
    } = selection;
    const { products, brandGroups, total } = data;
    const {
        aiAnalysisProgress,
        setAiAnalysisProgress,
        exportProgress,
        setExportProgress,
        autoReplyProgress,
        setAutoReplyProgress,
    } = progress;
    const { jobs: answerJobs, cancelActiveJobs, isCancelingActiveJobs } = reviewAnswerJobs;
    const {
        enqueueReviewSelectionMutation,
        waitForReviewSelectionReconcile,
        isReviewsDataReconciliationSelectionVisualRunning = false,
    } = transport;
    const aiAnalysisAvailability = featureAvailability.aiAnalysis;
    const aiAnswerAvailability = featureAvailability.aiAnswerGeneration;

    const { scheduleExportProgressCleanup, isAiAnalysisLoading, isExportLoading } = useActionButtonsControllerProgress({
        events,
        isSocketConnected,
        aiAnalysisProgress,
        exportProgress,
        autoReplyProgress,
        setExportProgress,
        setAutoReplyProgress,
    });
    const { getCurrentPickedReviews } = usePickedReviewsRef(pickedReviews);
    const { resolveSelectedBrandProductGroupIds } = useBrandProductGroupResolver(brandGroups);

    const combinedAnalysis = useCombinedAnalysisOrchestration({
        activeTable,
        pickedProducts,
        pickedBrandGroups,
        accessToken,
        resolveSelectedBrandProductGroupIds,
    });
    const viewModel = useActionButtonsViewModel({
        activeTable,
        pickedReviewsCount: pickedReviews.length,
        pickedProductsCount: pickedProducts.length,
        pickedBrandGroupsCount: pickedBrandGroups.length,
        allowResponseSending: featureControls.allowResponseSending,
        aiQuota,
        aiAnswerAvailability,
        aiAnalysisAvailability,
        isAiAnalysisLoading,
        isCombinedAnalysisLoading: combinedAnalysis.isCombinedAnalysisLoading,
        isGroupsMergeLoading: uiState.isGroupsMergeLoading,
    });

    const { startAiAnalysis, cancelAiAnalysis } = useAiAnalysisOrchestration({
        events,
        accessToken,
        filtersQuery,
        sort,
        selectionSessionId,
        isAiAnalysisLoading,
        isSocketConnected,
        setPickedReviews,
        setAiAnalysisProgress,
        enqueueReviewSelectionMutation,
        refetchReviewsAndProducts,
        onAiAnalysisFinalized: uiState.bumpReportSummaryRefreshToken,
    });
    const { exportCsv, exportProductsCsv, cancelExport } = useExportActions({
        activeTable,
        accessToken,
        filtersQuery,
        sort,
        selectionSessionId,
        pickedReviews,
        pickedProducts,
        pickedBrandGroups,
        isExportLoading,
        isSocketConnected,
        setExportProgress,
        scheduleExportProgressCleanup,
    });
    const { onAllPicked } = useSelectionActions({
        activeTable,
        accessToken,
        selectionSessionId,
        hasPickedProducts: viewModel.hasPickedProducts,
        hasPickedBrandGroups: viewModel.hasPickedBrandGroups,
        products,
        brandGroups,
        maxSelectedItems,
        total,
        filtersQuery,
        sort,
        setPickedProducts,
        setPickedBrandGroups,
        setPickedReviews,
        waitForReviewSelectionReconcile,
        enqueueReviewSelectionMutation,
        getCurrentPickedReviews,
        setIsAllPickLoading: uiState.setIsAllPickLoading,
    });
    const { groupPickedProducts, groupPickedBrands } = useGroupMergeActions({
        accessToken,
        selectionSessionId,
        setPickedProducts,
        setPickedBrandGroups,
        refetchReviewsAndProducts,
        setIsGroupsMergeLoading: uiState.setIsGroupsMergeLoading,
    });
    const groupMerge = useGroupMergeModalOrchestration({
        pickedProducts,
        pickedBrandGroups,
        products,
        brandGroups,
        onMergeProducts: groupPickedProducts,
        onMergeBrands: groupPickedBrands,
    });
    const { runAiAnalysisForSelectedReviews, runAiAnalysisForSelectedProducts } = useAiAnalysisActions({
        activeTable,
        accessToken,
        pickedReviews,
        pickedProducts,
        pickedBrandGroups,
        products,
        brandGroups,
        allowAiAnalysis: aiAnalysisAvailability.enabled,
        aiAnalysisDisabledReason: aiAnalysisAvailability.reason,
        resolveSelectedBrandProductGroupIds,
        startAiAnalysis,
    });

    return {
        activeTable,
        viewModel,
        uiState,
        actionHandlers: {
            onAllPicked,
            onExportCsv: exportCsv,
            onExportProductsCsv: exportProductsCsv,
            onCancelExport: cancelExport,
            onRunAiAnalysisForSelectedReviews: runAiAnalysisForSelectedReviews,
            onRunAiAnalysisForSelectedProducts: runAiAnalysisForSelectedProducts,
            onCancelAiAnalysis: () => {
                void cancelAiAnalysis();
            },
            onOpenCombinedAnalysis: combinedAnalysis.openCombinedAnalysis,
            onGroupPickedProducts: groupMerge.onGroupPickedProducts,
            onGroupPickedBrands: groupMerge.onGroupPickedBrands,
            onCancelActiveAnswerJobs: () => {
                void cancelActiveJobs(accessToken);
            },
        },
        combinedAnalysis,
        groupMerge: {
            isGroupMergeModalOpen: groupMerge.isGroupMergeModalOpen,
            closeGroupMergeModal: groupMerge.closeGroupMergeModal,
            groupMergeNameDraft: groupMerge.groupMergeNameDraft,
            setGroupMergeNameDraft: groupMerge.setGroupMergeNameDraft,
            submitGroupMergeForm: groupMerge.submitGroupMergeForm,
            isGroupMergeSubmitDisabled: groupMerge.isGroupMergeSubmitDisabled,
            groupMergeEntityLabel: groupMerge.groupMergeTargetType === 'brands' ? 'брендов' : 'продуктов',
        },
        progress: {
            isExportLoading,
            isSelectedItemsCountSyncing: isReviewsDataReconciliationSelectionVisualRunning,
            isAiAnalysisLoading,
            isAnswerJobsCancelLoading: isCancelingActiveJobs,
            hasActiveAnswerJobs: answerJobs.length > 0,
        },
        pickedCounts: {
            pickedReviewsCount: pickedReviews.length,
            pickedProductsCount: pickedProducts.length,
            pickedBrandGroupsCount: pickedBrandGroups.length,
        },
    };
};
